/**
 * MandantDocumentBox — generische Upload-Box für einen Document-Type.
 *
 * Wiederverwendbar pro DocumentType (BWA, EKS, Mietvertrag, Steuernummer-
 * Nachweis, Sonstiges).
 *
 * Modi:
 *   - 'single': pro Case max. ein aktives Dokument. Bei neuem Upload wird
 *     die alte Version DB-seitig durch den `replaced_by`-Trigger ersetzt
 *     (Migration 0016). Im UI: zeigen wir entweder die aktuelle Datei +
 *     „Andere Datei hochladen" ODER den leeren Drop-Zone.
 *   - 'multi': mehrere aktive Dokumente erlaubt. Liste mit „+ Weitere
 *     Datei hinzufügen"-Button. Wird für `sonstiges` verwendet.
 *
 * Der Upload nutzt `uploadCaseDocument()` aus supabaseDocuments.ts. Validation,
 * Storage + DB-Insert sind dort gekapselt — wir reagieren nur auf das Promise.
 *
 * Download nutzt `getDocumentDownloadUrl()` on-click — die signed-URL hat
 * 5-min-TTL und wird NICHT in `<a href>` gecached (würde sonst nach Ablauf
 * stale werden).
 *
 * Camera-Capture: zweiter file-input mit `capture="environment"` (Smartphones).
 *
 * Auf Desktop fungiert er als zweiter file-picker — akzeptables Fallback.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Camera, FileCheck2, Trash2, Upload, Download, X } from 'lucide-react';
import { de } from '../../lib/i18n/de';
import {
  DOCUMENT_TYPES,
  deleteCaseDocument,
  getDocumentDownloadUrl,
  linkBwaToFinancialDataset,
  listCaseDocuments,
  uploadCaseDocument,
  type CaseDocument,
  type DocumentType,
} from '../../lib/supabaseDocuments';
import {
  CASE_DOCUMENT_ACCEPTED_EXTENSIONS,
  CASE_DOCUMENT_ACCEPTED_MIMES,
  validateUploadFile,
  type DocumentValidationError,
} from '../../lib/supabaseDocumentValidation';
import { useConfirm } from '../ui/ConfirmDialog';
import { Button } from '../ui/Button';

export type DocumentBoxMode = 'single' | 'multi';

interface MandantDocumentBoxProps {
  readonly caseId: string;
  readonly docType: DocumentType;
  readonly mode: DocumentBoxMode;
  /**
   * Optional: extern gelieferte Liste. Wenn weggelassen oder undefined, lädt
   * die Box `listCaseDocuments` selbst beim Mount. null = explizit „keine
   * Daten zeigen". Praktisch für Tests + Performance (Parent-Komponente kann
   * eine Bulk-Query teilen).
   */
  readonly initialDocuments?: ReadonlyArray<CaseDocument> | null | undefined;
  /** Callback nach erfolgreichem Upload — Parent kann Liste neu laden. */
  readonly onChange?: () => void;
  /** Wenn true: Box wird als „nur Lesen" gerendert (Berater-Sicht). */
  readonly readOnly?: boolean;
}

type BoxState =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'uploading'; fileName: string }
  | { kind: 'error'; message: string };

const ACCEPT_ATTR = [...CASE_DOCUMENT_ACCEPTED_MIMES, ...CASE_DOCUMENT_ACCEPTED_EXTENSIONS].join(
  ',',
);

export function MandantDocumentBox({
  caseId,
  docType,
  mode,
  initialDocuments,
  onChange,
  readOnly = false,
}: MandantDocumentBoxProps) {
  const confirm = useConfirm();
  const [docs, setDocs] = useState<ReadonlyArray<CaseDocument>>(initialDocuments ?? []);
  const [state, setState] = useState<BoxState>(
    initialDocuments !== undefined ? { kind: 'ready' } : { kind: 'loading' },
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const safeSet = useCallback((next: BoxState) => {
    if (isMountedRef.current) {
      setState(next);
    }
  }, []);

  const refresh = useCallback(async () => {
    safeSet({ kind: 'loading' });
    try {
      const list = await listCaseDocuments(caseId, { onlyActive: true });
      const filtered = list.filter((d) => d.doc_type === docType);
      if (!isMountedRef.current) return;
      setDocs(filtered);
      safeSet({ kind: 'ready' });
    } catch (err) {
      safeSet({
        kind: 'error',
        message: err instanceof Error ? err.message : de.mandant.documents.errorUploadFailed,
      });
    }
  }, [caseId, docType, safeSet]);

  // Initial-Load nur wenn keine externen Docs übergeben wurden
  useEffect(() => {
    if (initialDocuments === undefined) {
      void refresh();
    } else if (initialDocuments === null) {
      setDocs([]);
    } else {
      setDocs(initialDocuments.filter((d) => d.doc_type === docType));
    }
  }, [initialDocuments, refresh, docType]);

  // -- Upload-Handler --
  const handleFile = useCallback(
    async (file: File) => {
      const v = validateUploadFile(file);
      if (!v.ok) {
        safeSet({ kind: 'error', message: messageFor(v.error) });
        return;
      }
      safeSet({ kind: 'uploading', fileName: file.name });
      try {
        const doc = await uploadCaseDocument({ caseId, docType, file });

        // Phase B (BWA-Konsolidierung):
        // Wenn docType='bwa', verknüpfen wir den Pfad zusätzlich mit
        // financial_dataset.raw_bwa_storage_path, damit der Score-Engine-
        // Reader weiterhin funktioniert. Fehler hier sind nicht-blockierend
        // (das case_documents-Insert ist erfolgreich gewesen) — wir loggen
        // sie nur. Der Berater sieht die BWA in der Doku-Liste und kann
        // notfalls Score-Engine-Verknüpfung manuell anstoßen.
        if (docType === 'bwa') {
          try {
            await linkBwaToFinancialDataset(caseId, doc.storage_path);
          } catch (linkErr) {
            console.warn(
              'MandantDocumentBox: linkBwaToFinancialDataset failed (non-blocking)',
              linkErr,
            );
          }
        }

        // Nach Upload Liste neu laden — Trigger 0016 hat ggf. die alte
        // Version durch replaced_by ersetzt, das spiegelt sich in der
        // gefilterten Liste sofort wider.
        await refresh();
        onChange?.();
      } catch (err) {
        safeSet({
          kind: 'error',
          message: err instanceof Error ? err.message : de.mandant.documents.errorUploadFailed,
        });
      }
    },
    [caseId, docType, refresh, onChange, safeSet],
  );

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void handleFile(file);
    }
  };

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void handleFile(file);
    }
    e.target.value = '';
  };

  const handleDelete = async (doc: CaseDocument) => {
    const proceed = await confirm({
      title: de.confirmDialog.removeDocumentTitle,
      body: de.mandant.documents.removeConfirm,
      confirmLabel: de.confirmDialog.removeDocumentConfirmLabel,
      tone: 'danger',
    });
    if (!proceed) {
      return;
    }
    try {
      await deleteCaseDocument(doc.id);
      await refresh();
      onChange?.();
    } catch (err) {
      safeSet({
        kind: 'error',
        message: err instanceof Error ? err.message : de.mandant.documents.errorRemoveFailed,
      });
    }
  };

  const handleDownload = async (doc: CaseDocument) => {
    try {
      const url = await getDocumentDownloadUrl(doc.id, 300);
      // Window.open: User hat den Klick getätigt, also nicht von Pop-up-
      // Blockern betroffen.
      if (typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      safeSet({
        kind: 'error',
        message: err instanceof Error ? err.message : de.mandant.documents.errorUploadFailed,
      });
    }
  };

  const meta = DOCUMENT_TYPES[docType];
  const hasAny = docs.length > 0;
  const showDropZone = !readOnly && state.kind !== 'uploading' && (mode === 'multi' || !hasAny);

  return (
    <div className="rounded-sm border border-stone-200 bg-white">
      <header className="border-b border-stone-100 px-5 py-4 md:px-6">
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {meta.label}
        </p>
        <p className="mt-1 font-serif text-sm text-stone-600">{meta.description}</p>
      </header>

      <div className="px-5 py-4 md:px-6">
        {/* Liste der hochgeladenen Dokumente */}
        {hasAny ? (
          <ul className="space-y-2">
            {docs.map((doc) => (
              <li
                key={doc.id}
                className="flex items-start gap-3 rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3"
              >
                <FileCheck2 size={18} className="mt-0.5 flex-shrink-0 text-emerald-700" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-serif text-sm font-medium text-emerald-900">
                    {doc.original_filename}
                  </p>
                  <p className="mt-0.5 font-sans text-[11px] uppercase tracking-widest text-emerald-700">
                    {de.mandant.documents.statusUploaded.replace(
                      '{date}',
                      new Date(doc.uploaded_at).toLocaleDateString('de-DE'),
                    )}
                    {' · '}
                    {formatBytes(doc.size_bytes)}
                  </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      void handleDownload(doc);
                    }}
                    aria-label={de.mandant.documents.downloadButton}
                    className="inline-flex h-9 min-w-[36px] items-center justify-center rounded-sm border border-emerald-300 bg-white px-2 font-sans text-[11px] font-medium uppercase tracking-widest text-emerald-800 hover:bg-emerald-100"
                  >
                    <Download size={12} />
                    <span className="ml-1 hidden sm:inline">
                      {de.mandant.documents.downloadButton}
                    </span>
                  </button>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => {
                        void handleDelete(doc);
                      }}
                      aria-label={de.mandant.documents.removeButton}
                      className="inline-flex h-9 min-w-[36px] items-center justify-center rounded-sm border border-emerald-300 bg-white px-2 font-sans text-[11px] font-medium uppercase tracking-widest text-emerald-800 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          state.kind === 'ready' && (
            <p className="font-serif text-sm italic text-stone-500">
              {de.mandant.documents.statusEmpty}
            </p>
          )
        )}

        {state.kind === 'loading' && (
          <p className="font-serif text-sm italic text-stone-500">Wird geladen …</p>
        )}

        {/* Drop-Zone für Upload */}
        {showDropZone && (
          <div
            onDragOver={(e) => {
              e.preventDefault();
            }}
            onDrop={handleDrop}
            className={`${hasAny ? 'mt-4' : 'mt-0'} flex flex-col items-stretch gap-2 rounded-sm border-2 border-dashed border-stone-300 bg-stone-50 px-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-3`}
          >
            <div className="flex items-center gap-2 sm:flex-1">
              <Upload size={18} className="text-stone-500" aria-hidden="true" />
              <span className="font-serif text-sm text-stone-600">
                {hasAny ? de.mandant.documents.addAnotherButton : de.mandant.documents.uploadButton}
              </span>
            </div>
            <div className="flex items-stretch gap-2 sm:flex-shrink-0">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  fileInputRef.current?.click();
                }}
                className="flex-1"
              >
                {de.mandant.documents.uploadButton}
              </Button>
              <button
                type="button"
                onClick={() => {
                  cameraInputRef.current?.click();
                }}
                className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-sm border border-stone-300 bg-white px-3 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 transition-colors hover:border-stone-900 hover:text-stone-900"
                aria-label={de.mandant.documents.cameraButton}
              >
                <Camera size={14} aria-hidden="true" />
                <span className="hidden sm:inline">{de.mandant.documents.cameraButton}</span>
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_ATTR}
              onChange={handleSelect}
              className="sr-only"
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleSelect}
              className="sr-only"
            />
          </div>
        )}

        {/* Format-Hinweis */}
        {!readOnly && state.kind !== 'uploading' && (
          <p className="mt-2 font-sans text-[11px] uppercase tracking-widest text-stone-500">
            {de.mandant.documents.acceptedFormats}
          </p>
        )}

        {/* Upload-In-Progress */}
        {state.kind === 'uploading' && (
          <div className="mt-3 flex items-center gap-3 rounded-sm border border-stone-200 bg-white px-4 py-3">
            <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-stone-400" />
            <p className="flex-1 truncate font-serif text-sm text-stone-700">
              {de.mandant.documents.uploading}{' '}
              <span className="text-stone-500">— {state.fileName}</span>
            </p>
          </div>
        )}

        {/* Fehler */}
        {state.kind === 'error' && (
          <div className="mt-3 flex items-start gap-3 rounded-sm border border-red-200 bg-red-50 px-4 py-3">
            <AlertCircle
              size={18}
              className="mt-0.5 flex-shrink-0 text-red-700"
              aria-hidden="true"
            />
            <p className="flex-1 font-serif text-sm text-red-900">{state.message}</p>
            <button
              type="button"
              onClick={() => {
                void refresh();
              }}
              aria-label="Schließen"
              className="rounded-sm p-1 text-red-700 hover:bg-red-100"
            >
              <X size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function messageFor(error: DocumentValidationError | undefined): string {
  switch (error) {
    case 'too-large':
      return de.mandant.documents.errorTooLarge;
    case 'wrong-type':
      return de.mandant.documents.errorWrongType;
    case 'empty':
      return de.mandant.documents.errorEmpty;
    default:
      return de.mandant.documents.errorUploadFailed;
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
